/**
 * TanStack Query hooks for `auto-alert-affiliate-via-email-in-app`. Thin wrappers
 * over ./api. Delivered-toggle invalidates list + detail cache.
 */
import {
	useMutation,
	useQuery,
	useQueryClient,
	type UseMutationResult,
	type UseQueryResult,
} from "@tanstack/react-query";
import {
	affiliateAlertQueryKeys,
	createAlert,
	listAlerts,
	markDelivered,
	type AffiliateAlertRow,
	type CreateAffiliateAlertBody,
	type CreateAffiliateAlertResponse,
	type ListAffiliateAlertsResponse,
	type MarkDeliveredResponse,
} from "./api";

export function useAffiliateAlertList(params?: {
	affiliate_id?: string;
	limit?: number;
}): UseQueryResult<ListAffiliateAlertsResponse, Error> {
	return useQuery({
		queryKey: affiliateAlertQueryKeys.list(params),
		queryFn: () => listAlerts(params),
	});
}

export function useAffiliateAlertCreate(): UseMutationResult<
	CreateAffiliateAlertResponse,
	Error,
	CreateAffiliateAlertBody
> {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: createAlert,
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: affiliateAlertQueryKeys.all });
		},
	});
}

export function useAffiliateAlertMarkDelivered(): UseMutationResult<
	MarkDeliveredResponse,
	Error,
	string
> {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: markDelivered,
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: affiliateAlertQueryKeys.all });
		},
	});
}

export type {
	AffiliateAlertRow,
	CreateAffiliateAlertBody,
	ListAffiliateAlertsResponse,
};
